/**
 * UnlockCode aggregate.
 *
 * A short code (typically 6-8 digits) the dealer / support staff can issue
 * to a customer to unlock a device offline. The code is:
 *   - Generated server-side with high-entropy randomness.
 *   - Stored only as a hash (argon2id) — we never persist the cleartext.
 *   - Time-bounded by `expiresAt` (default 24h).
 *   - Attempt-bounded by `attemptsRemaining` (default 3).
 *   - Bound to a specific `deviceId` so a leaked code cannot be used on
 *     another device.
 *
 * Maker-checker (per ADR-0011) applies to issuance: a support agent
 * requests a code, a supervisor approves, the platform mints + delivers.
 * That maker-checker workflow lives in apps/api (P1.A.4 / P1.A.6); the
 * domain layer only knows the resulting UnlockCode.
 */
import { z } from "zod";
import { err, ok } from "../result.js";
const TERMINAL_UNLOCK_STATES = new Set([
    "used",
    "expired",
    "revoked",
]);
export const unlockCodeSchema = z.object({
    id: z.string().uuid(),
    deviceId: z.string().uuid(),
    loanId: z.string().uuid(),
    codeHash: z.string().min(1),
    expiresAt: z.coerce.date(),
    attemptsRemaining: z.number().int().nonnegative(),
    issuedBy: z.string().min(1),
    approvedBy: z.string().min(1),
    status: z.enum(["issued", "used", "expired", "revoked"]),
    createdAt: z.coerce.date(),
    updatedAt: z.coerce.date(),
});
export function makeUnlockCode(raw) {
    const parsed = unlockCodeSchema.parse(raw);
    return {
        id: parsed.id,
        deviceId: parsed.deviceId,
        loanId: parsed.loanId,
        codeHash: parsed.codeHash,
        expiresAt: parsed.expiresAt,
        attemptsRemaining: parsed.attemptsRemaining,
        issuedBy: parsed.issuedBy,
        approvedBy: parsed.approvedBy,
        status: parsed.status,
        createdAt: parsed.createdAt,
        updatedAt: parsed.updatedAt,
    };
}
const MIN_REASON_LEN = 10;
export function transitionUnlockCode(code, event, now = new Date()) {
    if (TERMINAL_UNLOCK_STATES.has(code.status)) {
        return err({ kind: "TerminalState", current: code.status });
    }
    // Auto-expiry guard — any event hitting an already-expired code transitions
    // it to expired regardless of intent.
    if (code.expiresAt.getTime() < now.getTime()) {
        if (event.kind === "Expire") {
            return ok({ ...code, status: "expired", updatedAt: event.at });
        }
        return err({ kind: "AlreadyExpired" });
    }
    switch (event.kind) {
        case "AttemptUse": {
            if (code.attemptsRemaining <= 0) {
                return err({ kind: "NoAttemptsRemaining" });
            }
            const next = code.attemptsRemaining - 1;
            const newStatus = next === 0 ? "revoked" : "issued";
            return ok({
                ...code,
                attemptsRemaining: next,
                status: newStatus,
                updatedAt: event.at,
            });
        }
        case "MarkUsed":
            return ok({ ...code, status: "used", updatedAt: event.at });
        case "Expire":
            return ok({ ...code, status: "expired", updatedAt: event.at });
        case "Revoke":
            if (event.reason.length < MIN_REASON_LEN) {
                return err({ kind: "ReasonTooShort", minLength: MIN_REASON_LEN });
            }
            return ok({ ...code, status: "revoked", updatedAt: event.at });
    }
}
//# sourceMappingURL=unlock-code.js.map